import { isValidLinkUrl } from './urlValidation'

// The client identifies social platforms by a numeric code; the backend uses the Icon enum's
// lowercase NAME ("instagram"). The array index IS the client code and matches the backend
// enum order, so conversion is just an index lookup both ways.
export const ICON_NAMES = [
    'email',     // 0
    'instagram', // 1
    'facebook',  // 2
    'twitter',   // 3
    'tiktok',    // 4
    'pinterest', // 5
    'snapchat',  // 6
    'reddit',    // 7
    'whatsapp',  // 8
    'tumblr',    // 9
    'youtube',   // 10
    'twitch',    // 11
    'discord',   // 12
    'spotify',   // 13
    'amazon',    // 14
    'ebay',      // 15
    'etsy',      // 16
    'github',    // 17
    'linkedin',  // 18
    'paypal',    // 19
    'vine',      // 20
    'website',   // 21
]

export function iconCodeToName(code) {
    return ICON_NAMES[code] ?? 'website'
}

export function iconNameToCode(name) {
    const index = ICON_NAMES.indexOf(String(name).toLowerCase())
    return index === -1 ? 21 : index
}

// Base URL used to turn a bare handle ("hepsenbricks") into a full profile URL
// ("https://www.instagram.com/hepsenbricks"), keyed by platform name. Platforms with no reliable
// handle→URL form (amazon, vine, website) are intentionally absent — a bare value for those just
// gets "https://" prepended. Tumblr uses a `<handle>.tumblr.com` subdomain and is handled below.
export const SOCIAL_URL_BASES = {
    instagram: 'https://www.instagram.com/',
    facebook: 'https://www.facebook.com/',
    twitter: 'https://x.com/',
    tiktok: 'https://www.tiktok.com/@',
    pinterest: 'https://www.pinterest.com/',
    snapchat: 'https://www.snapchat.com/add/',
    reddit: 'https://www.reddit.com/user/',
    youtube: 'https://www.youtube.com/@',
    twitch: 'https://www.twitch.tv/',
    discord: 'https://discord.gg/',
    spotify: 'https://open.spotify.com/user/',
    ebay: 'https://www.ebay.com/usr/',
    etsy: 'https://www.etsy.com/shop/',
    github: 'https://github.com/',
    linkedin: 'https://www.linkedin.com/in/',
    paypal: 'https://www.paypal.me/',
}

const ensureHttps = (v) => (/^https?:\/\//i.test(v) ? v : `https://${v}`)

// The hostname of a base URL, minus a leading "www." (e.g. "instagram.com"). Used to recognise a
// value that is already a (scheme-less) URL for that platform.
function baseDomain(base) {
    try {
        return new URL(base).hostname.replace(/^www\./i, '')
    } catch {
        return ''
    }
}

// Treat the value as an already-typed URL (so we only prepend a scheme) rather than a bare handle.
// A dotted handle like "hepsen.bricks" (no "/", not the platform's own domain) stays a handle,
// while "instagram.com/hepsen" or "www.instagram.com" are recognised as URLs.
function looksLikeUrl(value, base) {
    const v = value.toLowerCase()
    if (v.includes('://') || /^www\./i.test(value)) return true
    const domain = base ? baseDomain(base) : ''
    if (domain && v.includes(domain)) return true
    return value.includes('.') && value.includes('/')
}

/**
 * Normalise a social-link value into a proper, openable URL for the given platform code.
 * Full URLs (with a scheme) are returned untouched; bare handles are expanded via SOCIAL_URL_BASES.
 */
export function normalizeSocialUrl(code, raw) {
    const value = (raw || '').trim()
    if (!value) return value

    // Already a full URL — trust whatever the user pasted.
    if (/^https?:\/\//i.test(value)) return value

    const name = iconCodeToName(code)

    if (name === 'email') {
        if (/^mailto:/i.test(value)) return value
        if (value.includes('@')) return `mailto:${value}`
        return ensureHttps(value)
    }

    if (name === 'whatsapp') {
        const digits = value.replace(/\D/g, '')
        return digits ? `https://wa.me/${digits}` : ensureHttps(value)
    }

    const base = SOCIAL_URL_BASES[name]

    // A scheme-less URL/domain (e.g. "instagram.com/hepsen") just needs "https://".
    if (looksLikeUrl(value, base)) return ensureHttps(value)

    // Strip a leading "@", a Reddit "u/" prefix, and any leading slashes to get the bare handle.
    const handle = value.replace(/^@+/, '').replace(/^u\//i, '').replace(/^\/+/, '')

    if (name === 'tumblr') return handle ? `https://${handle}.tumblr.com` : ensureHttps(value)
    if (base && handle) return `${base}${handle}`

    // amazon / vine / website / anything without a handle pattern → best-effort scheme prepend.
    return ensureHttps(value)
}

/**
 * True if the value a user entered for a social platform resolves to a real link once normalised.
 * Email is validated as a proper `mailto:` address; everything else must normalise to a valid URL.
 * (Reused by SocialLinkModal to reject code snippets / non-links before saving.)
 */
export function isValidSocialValue(code, raw) {
    const normalized = normalizeSocialUrl(code, raw)
    if (iconCodeToName(code) === 'email') {
        return /^mailto:[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(normalized)
    }
    return isValidLinkUrl(normalized)
}
